import { AnalyticsUpsellType } from '@/services/analytics/platforms/adobe-analytics/models';
import { CountryCode } from '@/store/ui';

export interface CmsImageUrls {
  default: string;
  desktop?: string;
  mobile?: string;
}

export enum CmsLinkLocation {
  Internal = 'internal', // vue route, e.g. /rewards
  External = 'external', // external to Vue, e.g. AEM pages and https://catering.chipotle.com
}

export enum CmsLinkTarget {
  Blank = '_blank',
  Self = '_self',
}

export type CmsLink =
  | {
      type: 'text';
      text: string;
      url: string;
      location: CmsLinkLocation;
      target: CmsLinkTarget;
    }
  | {
      type: 'button'; 
      text: string;
      enabled: boolean;
      location: CmsLinkLocation;
      target: CmsLinkTarget;
      url: string;
    }
  | {
      type: 'icon' | 'image';
      url: string;
      imageUrl: string;
      altText: string;
      location: CmsLinkLocation;
      target: CmsLinkTarget;
    };

export interface PromoBanner {
  showPromoBanner: boolean;
  showAdaptiveHero?: boolean | null;
  nooHomePageURL?: string;
  showNewHero?: boolean | null;
  showHero?: boolean | null;
  dualPromo: boolean;
  centerAlignedTextOnly: boolean;
  isContentRightAligned: boolean;
  shouldPlaceMobileTitleBelowImage: boolean;
  primary: {
    backgroundImageUrl?: string;
    backgroundVideoUrl?: string;
    shouldLoopVideo?: boolean;
    title?: string;
    preTitle?: string;
    preTitleColor?: string;
    titleColor?: string;
    subtitle?: string;
    subtitleColor?: string;
    legalText?: string;
    legalTextColor?: string;
    buttonText?: string;
    buttonLink?: string;
    buttonColor?: string;
    imageUrlDesktop?: string;
    imageUrlMobile?: string;
    videoUrlDesktop?: string;
    videoUrlMobile?: string;
    shouldLoopBottomVideo?: boolean;
    linkCategory?: string;
  };
  secondary?: {
    title?: string;
    titleColor?: string;
    subtitle?: string;
    subtitleColor?: string;
    legalText?: string;
    legalTextColor?: string;
    buttonText?: string;
    buttonLink?: string;
    buttonColor?: string;
    imageUrlDesktop?: string;
    imageUrlMobile?: string;
    videoUrlDesktop?: string;
    videoUrlMobile?: string;
    shouldLoopBottomVideo?: boolean;
    linkCategory?: string;
  };
}

export interface LargeOrderPromo {
  desktopBackgroundImageUrl?: string;
  mobileBackgroundImageUrl?: string;
  mobileBackgroundColor?: string;
  desktopBackgroundColor?: string;
  preTitle?: string;
  preTitleColor?: string;
  title?: string;
  titleColor?: string;
}

export interface GuacMode {
  message?: string;
}

export interface RewardsBanner {
  enableRewardsBanner: boolean;
  medallionImage: string;
  description: string;
  ctaSelecter: 'Standard' | 'DynamicLink';
  createAccountButtonLabel: string;
  dynamicCtaUrl: string;
  dynamicCtaButtonLabel: string;
  signInButtonLabel: string;
}

export interface LoyaltyInterruptModal {
  subCopy: string;
  salutation: string;
  howRewardsWorksCta: string;
  medallionImageUrl: string;
  mainCopy: string;
  joinNowCta: string;
}

export interface LargeOrderPromoCard {
  title?: string;
  titleColor?: string;
  backgroundColor?: string;
  imageUrl?: string;
  navUrl?: string;
  detailsCopy?: string;
  detailsCopyColor?: string;
  boldCopy?: string;
  boldCopyColor?: string;
  buttonText?: string;
  buttonTextColor?: string;
  buttonColor?: string;
  linkCategory?: string;
}

export interface JoinRewards {
  header: string;
  buttonShortText: string;
  buttonLongText: string;
  emailUpdates: string;
}

export interface LegalCopy {
  createAccount: LegalCopyCreateAccount,
  createAccountOrderConfirmation: LegalCopyCreateAccount;
  personalPreference: string;
  loggedInUnEnrolled: string;
  retroCredit: string;
}

export type LegalCopyCreateAccount = {
  [key in CountryCode]: string;
}

export interface Footer {
  primaryLinks: CmsLink[];
  secondaryLinks: {
    heading: string;
    links: CmsLink[];
  }[];
  tertiaryLinks: CmsLink[];
  cookiePrefs: CmsLink;
}

export interface UpsellItem {
  cmgId: string;
  MenuItemName?: string;
  imageUrl: string;
  sortOrderPosition?: number;
  shortName: string;
  name: string;
  price: string;
  upsellType: AnalyticsUpsellType;
}

export interface CustomerCare {
  message: string;
  linkText: string;
  linkUrl: string;
}

export interface SystemFlags {
  headerPages: string[];
}

export interface CreateAccountSignInContentFragment {
  isModalEnabled: boolean;
  heading?: string;
  subheading?: string;
  imageUrl?: string;
  text1?: string;
  text2?: string;
  p1?: string;
  p2?: string;
  buttonText?: string;
  buttonUrl?: string;
}

export interface FeaturedPCE {
  header: string;
  subHeader: string;
  restaurantSelectedHeader: string;
}

// Currently used in Order WEb <title> tag and <meta rel="description"> tag 
export interface OrderWebMetadata {
    title: string;
    description: string;
}

// Currently used in Order WEb <title> tag and <meta rel="description"> tag for Google SEO/search results
export interface OrderWebTitleDescription {
  title: string;
  description: string;
}

export interface RetroCreditImages {
  receiptZip: CmsImageUrls;
  receiptDate: CmsImageUrls;
  receiptCheckNumber: CmsImageUrls;
  receiptTotal: CmsImageUrls;
}

export interface PickupModality {
  dineInNotOpen: string;
  pickupOptions: PickupModalityOption[];
}

export interface PickupModalityOption {
  title: string;
  description: string;
  imageUrl: string;
}

export interface OrderConfirmationCreateAccountExperience {
  header: string;
  description: string;
  medallionImageUrl: string;
}
